import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Compass, Loader2, Sparkles, Clock, ListPlus, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const DIFFICULTY_TONE = {
  easy: "bg-hearth-sage/15 text-hearth-sageDark",
  medium: "bg-hearth-amber/20 text-[#7A5320]",
  advanced: "bg-hearth-coral/15 text-hearth-coral",
};

/**
 * LittleDetour — a small, quietly bold surprise-me feature.
 * Renders a pill button that, on click, asks the AI for ONE adventurous recipe
 * from an adjacent cuisine, then opens a modal with a "why this fits" note.
 *
 * Props:
 *   items?: string[]  — optional pantry hint. Omit to let backend pull the pantry.
 *   variant?: "primary" | "quiet"
 *   onAddedMissing?: () => void
 */
export default function LittleDetour({ items, variant = "primary", onAddedMissing }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recipe, setRecipe] = useState(null);
  const [error, setError] = useState(null);
  const [cuisines, setCuisines] = useState([]);

  const fetchDetour = async () => {
    setLoading(true); setError(null); setRecipe(null);
    try {
      const body = {
        seed: Date.now() % 100000, // gentle nudge so consecutive taps vary
        ...(items ? { items } : {}),
      };
      const { data } = await api.post("/recipes/adventure", body);
      if (data.error) setError(data.error);
      setRecipe(data.recipe || null);
      setCuisines(data.cuisines || []);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Couldn't find a detour right now.");
    } finally { setLoading(false); }
  };

  const start = async () => {
    setOpen(true);
    await fetchDetour();
  };

  const addMissing = async () => {
    const ings = recipe?.missing || [];
    if (!ings.length) { toast.info("Nothing missing — everything's already home."); return; }
    try {
      const { data } = await api.post("/recipes/add-missing", { ingredients: ings });
      if (data.count > 0) {
        toast.success(`Added ${data.count} ingredient${data.count === 1 ? "" : "s"} to grocery`);
        onAddedMissing?.();
      } else {
        toast.info("Everything's already on your grocery list");
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const rate = async (rating) => {
    if (!recipe) return;
    try {
      await api.post("/recipes/feedback", {
        recipe_title: recipe.title,
        rating,
        uses: recipe.have || [],
        cuisines,
        sub_regions: recipe.cuisine ? [recipe.cuisine] : [],
      });
      toast.success(rating === "like" ? "Noted — we'll wander this way again." : "Fair — we'll try another lane.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const btnClass = variant === "quiet"
    ? "rounded-full border border-hearth-line hover:bg-hearth-stone text-hearth-clayDark h-10 px-4"
    : "rounded-full bg-hearth-ink hover:bg-black text-hearth-cream h-10 px-5 shadow-sm transition-transform hover:-translate-y-0.5";

  return (
    <>
      <Button
        data-testid="little-detour-button"
        onClick={start}
        variant={variant === "quiet" ? "ghost" : "default"}
        className={btnClass}
      >
        <Compass size={14} className="mr-1.5" />
        A little detour
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="little-detour-dialog"
          className="rounded-3xl border-hearth-line bg-hearth-paper max-w-2xl max-h-[85vh] overflow-y-auto"
        >
          <DialogHeader>
            <div className="flex items-center gap-2 caption text-hearth-clayDark mb-1">
              <Compass size={13} /> a little detour
            </div>
            <DialogTitle className="font-display text-3xl font-semibold text-hearth-ink">
              {loading ? "Wandering into the kitchen…" : recipe ? recipe.title : "Somewhere new tonight."}
            </DialogTitle>
            <DialogDescription className="text-hearth-mute">
              {loading
                ? "Looking for a dish that's next-door to what you love."
                : recipe
                  ? (recipe.cuisine ? <>from <span className="text-hearth-ink font-medium">{recipe.cuisine}</span> cuisine</> : null)
                  : "One warm surprise, drawn from a cuisine you don't usually cook."}
            </DialogDescription>
          </DialogHeader>

          {loading && (
            <div className="py-10 flex flex-col items-center gap-3">
              <Loader2 size={28} className="animate-spin text-hearth-clay" />
              <p className="font-display italic text-lg text-hearth-mute">A gentle detour, coming up.</p>
            </div>
          )}

          {error && !loading && (
            <div className="warm-card p-5 border-hearth-coral/30 bg-hearth-coral/5 text-hearth-coral text-sm">
              {error.toLowerCase().includes("budget")
                ? "The recipe AI is on a coffee break — top up your Universal Key and try again."
                : "Couldn't fetch a detour right now — try again in a moment."}
            </div>
          )}

          {!loading && recipe && (
            <div className="space-y-5" data-testid="little-detour-content">
              {recipe.why_this_fits && (
                <p className="text-sm text-hearth-mute italic leading-relaxed border-l-2 border-hearth-clay/40 pl-4">
                  {recipe.why_this_fits}
                </p>
              )}

              <div className="flex items-center gap-3 flex-wrap">
                {recipe.time_min != null && (
                  <span className="caption inline-flex items-center gap-1.5"><Clock size={12}/> ~{recipe.time_min} min</span>
                )}
                {recipe.difficulty && (
                  <span className={`chip ${DIFFICULTY_TONE[recipe.difficulty] || DIFFICULTY_TONE.easy}`}>
                    {recipe.difficulty}
                  </span>
                )}
              </div>

              {recipe.ingredients?.length > 0 && (
                <div>
                  <h4 className="caption text-hearth-mute mb-2">Ingredients</h4>
                  <div className="flex flex-wrap gap-1.5" data-testid="little-detour-ingredients">
                    {recipe.ingredients.map((ing) => {
                      const have = (recipe.have || []).includes(ing);
                      return (
                        <span
                          key={ing}
                          className={`chip inline-flex items-center gap-1 ${
                            have
                              ? "bg-hearth-sage/15 text-hearth-sageDark"
                              : "bg-hearth-stone text-hearth-mute"
                          }`}
                        >
                          {have && <Sparkles size={10} />} {ing}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {recipe.steps?.length > 0 && (
                <div>
                  <h4 className="caption text-hearth-mute mb-2">Steps</h4>
                  <ol className="space-y-2 text-sm">
                    {recipe.steps.map((step, sidx) => (
                      <li key={sidx} className="flex gap-2">
                        <span className="w-5 h-5 rounded-full bg-hearth-stone text-hearth-ink text-xs font-mono grid place-items-center shrink-0 mt-0.5">
                          {sidx + 1}
                        </span>
                        <span className="text-hearth-ink leading-relaxed">{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="flex-wrap gap-2 sm:justify-between">
            <div className="flex gap-2">
              {recipe && (
                <>
                  <Button
                    data-testid="little-detour-like"
                    variant="ghost"
                    onClick={() => rate("like")}
                    className="rounded-full border border-hearth-line hover:bg-hearth-sage/10 h-9 text-xs"
                  >
                    I&apos;d try this
                  </Button>
                  <Button
                    data-testid="little-detour-dislike"
                    variant="ghost"
                    onClick={() => rate("dislike")}
                    className="rounded-full border border-hearth-line hover:bg-hearth-coral/10 h-9 text-xs"
                  >
                    Not tonight
                  </Button>
                </>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                data-testid="little-detour-again"
                onClick={fetchDetour}
                disabled={loading}
                variant="ghost"
                className="rounded-full border border-hearth-line hover:bg-hearth-stone h-9 text-xs"
              >
                {loading ? <Loader2 size={12} className="mr-1.5 animate-spin"/> : <RefreshCw size={12} className="mr-1.5"/>}
                Somewhere else
              </Button>
              {recipe?.missing?.length > 0 && (
                <Button
                  data-testid="little-detour-add-missing"
                  onClick={addMissing}
                  className="rounded-full bg-hearth-clay hover:bg-hearth-clayDark text-white h-9 text-xs"
                >
                  <ListPlus size={12} className="mr-1.5"/>
                  Add {recipe.missing.length} to grocery
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
